import React, {
  ReactElement,
  useRef,
  useState,
  useEffect,
  FocusEvent,
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
} from 'react';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import { DayPickerProps } from 'react-day-picker/types/Props';
import { RangeModifier } from 'react-day-picker/types/Modifiers';
import { ClassNames } from 'react-day-picker/types/ClassNames';
import { DateUtils } from 'react-day-picker';
import {
  Copy,
  SvgCalendar,
  Button,
  SvgX,
  Square,
  Stack,
  Flex,
} from '@grubhubprod/gh-cookbook-react/dist';
import { format as dateFormat, parse as dateParse, addDays } from 'date-fns';
import {
  useDeviceSize,
  Breakpoint,
} from '@grubhubprod/umami-library/dist/hooks/useDeviceSize';
import classnames from 'classnames';
import defaultClassNames from 'react-day-picker/build/classNames';
import { getWindow } from 'src/utilities/window';
import { Modal } from 'src/components/Modal/Modal';
import { IInputProps, Input } from 'src/components/Input/Input';
import 'react-day-picker/lib/style.css';
import { useClickOutside } from './hooks/useClickOutside';

export interface IDatePickerInputProps extends IInputProps {
  dayPickerProps: DayPickerProps; // https://react-day-picker.js.org/api/DayPicker/
  useFullScreenOverlay?: boolean;
  chooseConsecutiveDays?: boolean;
  numberOfConsecutiveDays?: number;
  isRangeSelection?: boolean;
  onDateChange: (startDate: Date, endDate?: Date) => void;
  initialDate?: Date;
  initialDateRange?: RangeModifier;
  forceMonthsToWrap?: boolean;
  blockWidth?: boolean;
  overlayComponent?: React.ComponentType<DefaultOverlayProps>;
}

export const DATE_FORMAT = 'MM/dd/yyyy';
export const DATE_FORMAT_YYYY_MM_DD = 'yyyy-MM-dd';

export interface DefaultOverlayProps {
  children: React.ReactNode;
  classNames: Record<string, string>;
  selectedDay: Date;
  month: Date;
  input: HTMLInputElement;
  tabIndex: number;
  onBlur: () => void;
  onFocus: (e: React.FocusEvent) => void;
}

type CompleteRangeModifier = Required<Pick<RangeModifier, 'from' | 'to'>>;

export interface IDatePickerInputComponentProps {
  onBlur: (e: FocusEvent<HTMLInputElement>) => void;
  onClick: (e: MouseEvent<HTMLInputElement>) => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp: (e: KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  value: string;
}

const DATE_RANGE_DELIMETER = ' - ';
function parseDate(dateString: string, format: string, locale: string): Date {
  let dateForParsing = dateString;
  const isDateRange = dateString.includes(DATE_RANGE_DELIMETER);

  if (isDateRange) {
    const [startDate] = dateString.split(DATE_RANGE_DELIMETER);
    dateForParsing = startDate;
  }

  return dateParse(dateForParsing, format, new Date(), {
    locale: locale as unknown as Locale,
  });
}

function isCompleteRange(
  range?: RangeModifier
): range is CompleteRangeModifier {
  return Boolean(range?.from && range?.to);
}

function renderDay(day: Date): JSX.Element {
  return <div className="dayContent">{day.getDate()}</div>;
}

export function DatePickerInput({
  dayPickerProps = {},
  onDateChange,
  onBlur,
  useFullScreenOverlay,
  chooseConsecutiveDays,
  numberOfConsecutiveDays = 6,
  isRangeSelection = false,
  placeholder,
  label,
  ariaLabel,
  name,
  id,
  error,
  initialDate,
  initialDateRange,
  forceMonthsToWrap,
  disabled,
  blockWidth = false,
  overlayComponent,
}: IDatePickerInputProps): JSX.Element {
  const dayPickerInputRef = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { breakpoint } = useDeviceSize(getWindow());
  const isExtraLargeViewport = breakpoint >= Breakpoint.xl;
  const [selectedDay, setSelectedDay] = useState<Date>(undefined);
  const [dateRange, setDateRange] = useState<RangeModifier>({
    from: undefined,
    to: undefined,
  });
  const [committedDateRange, setCommittedDateRange] = useState<RangeModifier>(
    initialDateRange?.from && initialDateRange?.to
      ? initialDateRange
      : { from: undefined, to: undefined }
  );

  useEffect(() => {
    setSelectedDay(initialDate);
  }, [chooseConsecutiveDays, initialDate, numberOfConsecutiveDays]);

  useEffect(() => {
    if (initialDateRange) {
      setDateRange(initialDateRange);

      if (initialDateRange.from && initialDateRange.to) {
        setCommittedDateRange(initialDateRange);
      }
    }
  }, [chooseConsecutiveDays, initialDateRange]);

  function closeOverlay() {
    if (dayPickerInputRef.current) {
      dayPickerInputRef.current.hideDayPicker();
    }
  }

  useClickOutside(containerRef, closeOverlay, { enabled: !!isRangeSelection });

  function calculateDateRange(startDate: Date): {
    range: RangeModifier;
    endDate: Date;
  } {
    const endDate = addDays(startDate, numberOfConsecutiveDays);

    return {
      range: {
        from: startDate,
        to: endDate,
      },
      endDate,
    };
  }

  function handleDayChange(day: Date): void {
    if (chooseConsecutiveDays) {
      const { range, endDate } = calculateDateRange(day);
      setDateRange(range);
      onDateChange(day, endDate);

      // let the selected days show for a little before closing the picker
      closeOverlayAfterDelay();
    } else if (isRangeSelection) {
      handleRangeChange(day);
    } else {
      onDateChange(day);
    }

    if (!isRangeSelection) {
      setSelectedDay(day);
    }
  }

  function handleRangeChange(day: Date): void {
    const { from, to } = dateRange;

    const baseRange =
      from && to ? { from: undefined, to: undefined } : dateRange;
    const newRange = DateUtils.addDayToRange(day, baseRange);

    setDateRange(newRange);

    if (newRange.from && newRange.to) {
      setCommittedDateRange(newRange);
      onDateChange(newRange.from, newRange.to);
      closeOverlayAfterDelay();
    }
  }

  function closeOverlayAfterDelay(delay = 500): void {
    setTimeout(() => {
      closeOverlay();
    }, delay);
  }

  function formatRangeValue(range?: RangeModifier): string {
    if (!isCompleteRange(range)) return '';
    const start = dateFormat(range.from, DATE_FORMAT);
    const end = dateFormat(range.to, DATE_FORMAT);
    return `${start}${DATE_RANGE_DELIMETER}${end}`;
  }

  function formatDateValue(): string | Date {
    if (isRangeSelection) {
      return formatRangeValue(committedDateRange);
    }
    if (selectedDay) {
      return selectedDay;
    }
    return formatRangeValue(dateRange);
  }

  function formatDate(date: Date, format: string, locale: string): string {
    if (chooseConsecutiveDays) {
      const { range } = calculateDateRange(date);
      const startDate = dateFormat(range.from, format, {
        locale: locale as unknown as Locale,
      });

      const endDate = dateFormat(range.to, format, {
        locale: locale as unknown as Locale,
      });

      return `${startDate}${DATE_RANGE_DELIMETER}${endDate}`;
    }

    return dateFormat(date, format, { locale: locale as unknown as Locale });
  }

  const hasFullRange = Boolean(dateRange.from && dateRange.to);
  const modifiers = {
    selectedDateRange: dateRange,
    selectedDateRangeStart: hasFullRange ? dateRange.from : undefined,
    selectedDateRangeEnd: hasFullRange ? dateRange.to : undefined,
  };

  const Overlay = React.useCallback((overlayProps) => {
    return (
      <CustomOverlay
        closeOverlay={closeOverlay}
        onBlur={overlayProps.onBlur}
        onFocus={overlayProps.onFocus}
        tabIndex={overlayProps.tabIndex}
      >
        {overlayProps.children}
      </CustomOverlay>
    );
  }, []);

  const resolvedOverlay = useFullScreenOverlay ? Overlay : overlayComponent;

  function handleDayPickerHide(): void {
    if (isRangeSelection && dateRange.from && !dateRange.to) {
      setDateRange({
        from: committedDateRange.from,
        to: committedDateRange.to,
      });
    }
  }

  return (
    <Stack ref={containerRef}>
      <DayPickerInput
        inputProps={{
          ref: null,
        }}
        ref={dayPickerInputRef}
        format={DATE_FORMAT}
        formatDate={formatDate}
        parseDate={parseDate}
        keepFocus={!useFullScreenOverlay}
        hideOnDayClick={
          !(useFullScreenOverlay || chooseConsecutiveDays || isRangeSelection)
        }
        dayPickerProps={{
          classNames: {
            ...defaultClassNames,
            months: classnames(['months'], {
              wrap: !isExtraLargeViewport || forceMonthsToWrap,
            }),
          } as unknown as ClassNames,
          renderDay,
          selectedDays: isRangeSelection
            ? [dateRange.from, dateRange]
            : [selectedDay, dateRange],
          modifiers,
          ...dayPickerProps,
        }}
        placeholder={placeholder}
        {...(blockWidth && { style: { display: 'block' } })}
        component={(componentProps: IDatePickerInputComponentProps) => (
          <Input
            label={label}
            ariaLabel={ariaLabel}
            name={name}
            id={id}
            prefixIcon={
              <Copy variant="body" color="secondaryText">
                <SvgCalendar size="24px" />
              </Copy>
            }
            datePickerInputComponentProps={{
              ...componentProps,
              ...(isRangeSelection && { value: formatDateValue() as string }),
              onBlur: (e: FocusEvent<HTMLInputElement>) => {
                componentProps.onBlur(e);
                if (onBlur) {
                  onBlur(e);
                }
              },
            }}
            error={error}
            disabled={disabled}
            blockWidth={blockWidth}
          />
        )}
        value={formatDateValue()}
        onDayChange={handleDayChange}
        onDayPickerHide={handleDayPickerHide}
        {...(resolvedOverlay && { overlayComponent: resolvedOverlay })}
      />
    </Stack>
  );
}

interface ICustomOverlayProps {
  children: ReactElement;
  tabIndex: number;
  onBlur: () => void;
  onFocus: () => void;
  closeOverlay: () => void;
}

function CustomOverlay({
  children,
  tabIndex,
  onBlur,
  onFocus,
  closeOverlay,
}: ICustomOverlayProps): JSX.Element {
  return (
    <Modal size="xs">
      <div
        tabIndex={tabIndex}
        onBlur={onBlur}
        onFocus={onFocus}
        style={{ width: '100%' }}
      >
        <Flex flexDirection="column">
          <header>
            <Button variant="tertiary" type="button" onClick={closeOverlay}>
              <SvgX size="24px" />
            </Button>
            <Square inset="md" borderBottom="1px solid" borderColor="lightLine">
              <Copy variant="largeLead" fontFamily="secondary">
                Select a date
              </Copy>
            </Square>
          </header>
          <Flex
            justifyContent="center"
            flexGrow={1}
            margin="auto"
            overflowY="auto"
          >
            {children}
          </Flex>
          <Square inset="md" dimension="2">
            <Stack y="x-sm">
              <Button
                type="button"
                variant="primary"
                block
                onClick={closeOverlay} // TODO: adjust to actually only save on click?
              >
                Save
              </Button>
            </Stack>
            <Button
              type="button"
              variant="secondary"
              block
              onClick={closeOverlay}
            >
              Cancel
            </Button>
          </Square>
        </Flex>
      </div>
    </Modal>
  );
}
